/* Locale-aware formatting helpers. */

import { get } from "svelte/store";
import { locale as appLocale } from "svelte-i18n";
import { auth } from "./auth";

/** How many decimals this company writes catalogue prices with — 2 (12.00) or
 *  3 (12.000), from Prices → Settings. Two unless they said otherwise.
 *
 *  Money elsewhere in the app (orders, invoices, the back office) stays at two
 *  regardless: this is about how a price list reads, not about accounting. */
export function priceDecimals(tenantSlug?: string): number {
  const memberships = get(auth).memberships ?? [];
  const m = tenantSlug
    ? memberships.find((x) => x.tenant.slug === tenantSlug)
    : memberships[0];
  const d = m?.tenant?.price_decimals;
  return d === 3 ? 3 : 2;
}

/** EUR formatter bound to the app locale (svelte-i18n) — the single
 *  replacement for the ad-hoc `"€" + n.toFixed(2)` pattern.
 *
 *  `decimals` overrides the two-decimal default; pass `priceDecimals(slug)`
 *  on catalogue surfaces so a company pricing per kilo can show 12.000. */
export function formatEur(amount: number | string, decimals = 2): string {
  const loc = get(appLocale) || "en-GB";
  return new Intl.NumberFormat(loc, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(amount));
}

export function formatMoney(amount: number, currency: string, locale = "en-GB"): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

export function formatNumber(n: number, locale = "en-GB"): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** Barcodes/EANs imported from Excel or CSV sometimes arrive as a whole number
 *  written as a float ("4744890000000.0"). Render those as plain integer digits
 *  so a barcode never shows a decimal tail. Scientific notation ("4.74489E+12")
 *  is left as-is: its low-order digits are already lost, so expanding it would
 *  fabricate a plausible-but-wrong code. Non-numeric codes (letters, dashes) and
 *  plain digit strings are returned unchanged. String ops only — Number()/BigInt
 *  would corrupt codes longer than 2^53. */
export function formatBarcode(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value).trim();
  if (s === "") return "";
  // Already plain digits — keep verbatim (preserves any leading zeros).
  if (/^\d+$/.test(s)) return s;
  // Whole number written as a float ("123.0", "123.000") — drop the zero fraction.
  const m = /^(\d+)\.0+$/.exec(s);
  if (m) return m[1];
  return s;
}

/* Dates are written day first everywhere (owner, 2026-09-29: «везде формат
   день / месяц / год»): numerically as dd.mm.yyyy — the form the documents,
   PDFs and reports print — and with a month name as «29 сент. 2026». The
   interface language names the month but never decides the order: left to
   Intl, English would put the month first and Hungarian or Lithuanian the
   year. */

const pad2 = (n: number | string) => String(n).padStart(2, "0");

/** English month names in the British order («29 Sept 2026»). */
function dateLocale(locale?: string | null): string {
  const loc = locale || get(appLocale) || "en-GB";
  return loc === "en" || loc.startsWith("en-") ? "en-GB" : loc;
}

/** A date's parts in `locale` (month names in their proper case), put back
 *  together day → month → year, then the time. Parts the options leave out
 *  stay out; numeric months give «dd.mm.yyyy». */
export function formatDayFirst(
  value: Date | string,
  options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" },
  locale?: string | null,
  timeZone?: string,
): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "-";
  const withTime = options.hour !== undefined || options.minute !== undefined;
  const parts = new Intl.DateTimeFormat(dateLocale(locale), {
    ...options,
    ...(withTime ? { hourCycle: "h23" as const } : {}),
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const numericMonth = !options.month || options.month === "numeric" || options.month === "2-digit";
  const day = part("day"), month = part("month"), year = part("year");
  const datePart = numericMonth
    ? [day && pad2(day), month && pad2(month), year].filter(Boolean).join(".")
    : [day, month, year].filter(Boolean).join(" ");
  if (!withTime) return datePart;
  const time = [part("hour"), part("minute")].filter(Boolean).map(pad2).join(":");
  return [datePart, time].filter(Boolean).join(" ");
}

/** dd.mm.yyyy. A bare «YYYY-MM-DD» is read as that calendar day, never
 *  shifted by the browser's timezone. */
export function formatDate(d: Date | string): string {
  if (typeof d === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  return formatDayFirst(d);
}

/** dd.mm.yyyy HH:MM, in the browser's timezone. */
export function formatDateTime(d: Date | string): string {
  return formatDayFirst(d, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** «YYYY-MM-DD» → «dd.mm.yyyy» for a date field; "" stays "". */
export function isoToDay(iso: string | null | undefined): string {
  return iso ? formatDate(iso.slice(0, 10)) : "";
}

/** What a person typed into a date field → «YYYY-MM-DD»; "" for nothing,
 *  null for something that is no date. Day first: «29.09.2026», «29/9/26»,
 *  «29-09-2026», «29092026», «290926». */
export function dayToIso(text: string): string | null {
  const t = text.trim();
  if (!t) return "";
  let d: number, m: number, y: number;
  const sep = /^(\d{1,2})\s*[.\/\-\s]\s*(\d{1,2})\s*[.\/\-\s]\s*(\d{2}|\d{4})\.?$/.exec(t);
  const digits = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(t);
  const match = sep ?? digits;
  if (!match) return null;
  [d, m, y] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (match[3].length === 2) y += 2000;
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

/** The IANA timezone of the active company. Business timestamps must follow
 * this setting so every team member sees the same calendar day and time. */
export function companyTimezone(tenantSlug?: string): string {
  const memberships = get(auth).memberships ?? [];
  const membership = tenantSlug
    ? memberships.find((item) => item.tenant.slug === tenantSlug)
    : memberships[0];
  return membership?.tenant.timezone || "UTC";
}

/** Format a stored instant in the company's timezone, never the browser's. */
export function formatCompanyInstant(
  value: Date | string,
  tenantSlug?: string,
  options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  },
): string {
  return formatDayFirst(value, options, null, companyTimezone(tenantSlug));
}

/** A Date carrying the company's current calendar date in its UTC fields.
 * Consumers use UTC date arithmetic so the browser timezone cannot move a
 * "today", month, week, or year filter across a boundary. */
export function companyCalendarDate(tenantSlug?: string, value = new Date()): Date {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: companyTimezone(tenantSlug),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(value);
  const number = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return new Date(Date.UTC(number("year"), number("month") - 1, number("day")));
}

/** YYYY-MM-DD for a calendar Date created by companyCalendarDate(). */
export function calendarIsoDate(value: Date): string {
  const year = value.getUTCFullYear();
  const month = String(value.getUTCMonth() + 1).padStart(2, "0");
  const day = String(value.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
